import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, COMPANY } from "@/data";
import { trackConversion } from "@/components/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Services available in the quote form (excluding "Detailing Automobile" and "Préparation Esthétique")
export const FORM_SERVICES = [
  ...SERVICES.filter((s) => s.id !== "detailing" && s.id !== "preparation"),
  { id: "nettoyage-int-ext", title: "Nettoyage intérieur / extérieur" },
];

const SOURCES = [
  "Google",
  "Facebook-Instagram",
  "Bouche-à-oreille",
  "Avis Google",
  "Autre",
];

const GADS_SEND_TO = "AW-18463610714/1msZCKWinZQdENqukeRE";

// Normalise a Belgian phone number to E.164 (+32...). Already-international numbers are kept.
export const toE164 = (raw) => {
  let p = (raw || "").replace(/[\s().\-/]/g, "");
  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (p.startsWith("+")) return p;
  if (p.startsWith("0")) return "+32" + p.slice(1);
  return p ? "+32" + p : "";
};

// Fires the Google Ads conversion (with enhanced-conversion user_data) and resolves
// once gtag confirms the hit OR after a 1s safety timeout, so the redirect never loses it.
export const fireAdsConversion = (email, phone) =>
  new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const timer = setTimeout(finish, 1000);
    try {
      // Guarantee window.gtag exists (falls back to the standard dataLayer queue if gtag.js is slow).
      if (typeof window.gtag !== "function") {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
      }
      const userData = {};
      const cleanEmail = (email || "").trim().toLowerCase();
      const cleanPhone = toE164(phone);
      if (cleanEmail) userData.email = cleanEmail;
      if (cleanPhone) userData.phone_number = cleanPhone;
      window.gtag("set", "user_data", userData);
      window.gtag("event", "conversion", {
        send_to: GADS_SEND_TO,
        event_callback: () => {
          clearTimeout(timer);
          finish();
        },
      });
    } catch (err) {
      console.error("Erreur envoi conversion Google Ads", err);
      finish();
    }
  });

// Storage can throw (Safari "block all cookies", private mode, in-app browsers) → never let it crash the UI.
const safeStorage = {
  get: (store, k) => {
    try {
      return window[store].getItem(k);
    } catch {
      return null;
    }
  },
  set: (store, k, v) => {
    try {
      window[store].setItem(k, v);
    } catch {
      /* ignore */
    }
  },
};

// Visitor arrived from a Google Ads / Google campaign (gclid or utm_source=google) → remembered for the session.
export const isGoogleVisitor = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has("gclid")) safeStorage.set("sessionStorage", "vw_gclid", "1");
  if (params.has("gclid") || (params.get("utm_source") || "").toLowerCase() === "google") {
    safeStorage.set("sessionStorage", "vw_from_google", "1");
    return true;
  }
  return safeStorage.get("sessionStorage", "vw_from_google") === "1";
};

// Attribution fields sent with every lead (quote form + appointment card) for the CRM export.
export const leadMeta = (type) => ({
  type,
  page: typeof window === "undefined" ? "" : window.location.pathname,
  gclid:
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).has("gclid") || safeStorage.get("sessionStorage", "vw_gclid") === "1"),
});

const EMPTY_FORM = (service, source) => ({
  nom: "",
  email: "",
  telephone: "",
  vehicule: "",
  service,
  source,
});

const QuoteForm = ({ defaultService }) => {
  const navigate = useNavigate();
  const allowedDefault =
    defaultService && FORM_SERVICES.some((s) => s.title === defaultService)
      ? defaultService
      : FORM_SERVICES[0].title;
  const [fromGoogle] = useState(() => isGoogleVisitor());
  const defaultSource = fromGoogle ? "Google" : "";

  const [form, setForm] = useState(EMPTY_FORM(allowedDefault, defaultSource));
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, { ...form, message: "", ...leadMeta("form") });
      // Server confirmed success → fire Google Ads conversion exactly once, BEFORE redirecting.
      await fireAdsConversion(form.email, form.telephone);
      trackConversion("generate_lead", {
        service: form.service,
        value: 1,
        currency: "EUR",
      });
      setForm(EMPTY_FORM(allowedDefault, defaultSource));
      // Signal Confirmation page that this navigation comes from a real form success
      // (so the Google Ads conversion event only fires after an actual submit).
      navigate("/demande-envoyee", { state: { fromForm: true }, replace: true });
    } catch (err) {
      toast.error(
        "Une erreur est survenue lors de l'envoi. Vous pouvez nous joindre au " +
          COMPANY.phone_display +
          " ou par email à " +
          COMPANY.email
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="grid sm:grid-cols-2 gap-x-8 gap-y-2"
    >
      <Field
        label="Nom complet"
        value={form.nom}
        onChange={update("nom")}
        required
        testid="input-nom"
      />
      <Field
        label="Email (facultatif)"
        type="email"
        value={form.email}
        onChange={update("email")}
        testid="input-email"
      />
      <Field
        label="Téléphone"
        value={form.telephone}
        onChange={update("telephone")}
        required
        testid="input-telephone"
      />
      <Field
        label="Véhicule (marque & modèle)"
        value={form.vehicule}
        onChange={update("vehicule")}
        required
        testid="input-vehicule"
      />
      <div className="sm:col-span-2 mt-4">
        <label className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
          Service souhaité
        </label>
        <select
          data-testid="input-service"
          value={form.service}
          onChange={update("service")}
          className="vw-input"
        >
          {FORM_SERVICES.map((s) => (
            <option key={s.id} value={s.title}>
              {s.title}
            </option>
          ))}
        </select>
      </div>
      {!fromGoogle && (
        <div className="sm:col-span-2 mt-4">
          <label className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
            Comment nous avez-vous connu ? (facultatif)
          </label>
          <select
            data-testid="input-source"
            value={form.source}
            onChange={update("source")}
            className="vw-input"
          >
            <option value="">Sélectionnez une réponse</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="sm:col-span-2 mt-10">
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-quote"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#D90000] hover:bg-[#FF1A1A] disabled:opacity-60 text-white text-[12px] uppercase tracking-[0.25em] font-semibold px-12 py-5 transition-colors btn-sweep"
        >
          {submitting ? "Envoi..." : "Envoyer ma demande"}
          <ArrowUpRight size={16} />
        </button>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-white/40">
          <span className="flex items-center gap-2">
            <Check size={12} className="text-[#D90000]" /> Sans engagement
          </span>
          <span className="flex items-center gap-2">
            <Check size={12} className="text-[#D90000]" /> Réponse rapide par WhatsApp, lun–sam 9h–18h
          </span>
          <span className="flex items-center gap-2">
            <Check size={12} className="text-[#D90000]" /> Conseil expert
          </span>
        </div>
      </div>
    </form>
  );
};

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div className="mt-4">
    <label className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
      {label}
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="vw-input"
    />
  </div>
);

export default QuoteForm;
